import { createContext, useContext, ReactNode, useEffect, useState, useCallback } from "react";
import { useApp } from "./AppContext";

/**
 * mail message type
 */
export interface MailMessage {
  id: string;
  from: string;
  to: string;
  subject: string;
  body?: string;
  timestamp?: number;
  isRead: boolean;
}

export interface MailContextType {
  loading: boolean;
  error: string | null;
  emails: MailMessage[];

  // actions
  fetchMail: () => void;
  readEmail: (emailId: string) => void;
  sendEmail: (to: string, subject: string, body: string) => void;
  clearError: () => void;
}

const MailContext = createContext<MailContextType | undefined>(undefined);

export function useMail() {
  const context = useContext(MailContext);
  if (!context) {
    throw new Error("useMail must be used within a MailProvider");
  }
  return context;
}

export default function MailProvider({ children }: { children: ReactNode }) {
  const { sessionReady, lastAuthTime, onMessage, sendMessage } = useApp();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emails, setEmails] = useState<MailMessage[]>([]);

  // ===== DATA FETCHING =====
  const fetchMail = useCallback(() => {
    if (!sessionReady) {
      console.warn("[Mail] cannot fetch: session not ready");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      sendMessage("mail", "get_mail", {});
    } catch (err) {
      console.error("[Mail] failed to fetch:", err);
      setError("failed to fetch mail");
      setLoading(false);
    }
  }, [sessionReady, sendMessage]);

  // ===== MESSAGE HANDLERS =====
  const handleMailList = useCallback((message: any) => {
    // only handle mail service messages
    if (message.service !== "mail") return;

    if (message.action === "mail_list") {
      const mailData = message.payload?.mail || [];
      setEmails(mailData);
      setLoading(false);
      return;
    }

    if (message.action === "email_sent") {
      // refresh mail list after sending
      if (sessionReady) {
        sendMessage("mail", "get_mail", {});
      }
      return;
    }

    if (message.action === "error") {
      const errorMessage = message.payload?.message || "unknown error";
      console.error("[Mail] error:", errorMessage);
      setError(errorMessage);
      setLoading(false);
      // auto-clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
      return;
    }
  }, [sessionReady, sendMessage]);

  // read specific email
  const readEmail = useCallback(
    (emailId: string) => {
      if (!sessionReady) {
        console.warn("[Mail] cannot read: session not ready");
        return;
      }

      try {
        sendMessage("mail", "read_email", { emailId });
      } catch (err) {
        console.error("[Mail] failed to read:", err);
        setError("failed to read email");
      }
    },
    [sessionReady, sendMessage],
  );

  // send email
  const sendEmail = useCallback(
    (to: string, subject: string, body: string) => {
      if (!sessionReady) {
        console.warn("[Mail] cannot send: session not ready");
        return;
      }

      try {
        sendMessage("mail", "send_email", { to, subject, body });
      } catch (err) {
        console.error("[Mail] failed to send:", err);
        setError("failed to send email");
      }
    },
    [sessionReady, sendMessage],
  );

  // clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ===== SUBSCRIPTIONS =====
  useEffect(() => {
    const unsub1 = onMessage("mail_list", handleMailList);
    const unsub2 = onMessage("email_sent", handleMailList);
    const unsub3 = onMessage("error", handleMailList);

    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [onMessage, handleMailList]);

  // ===== AUTO-FETCH ON SESSION READY OR RECONNECT =====
  useEffect(() => {
    if (sessionReady) {
      fetchMail();
    }
  }, [sessionReady, lastAuthTime, fetchMail]);

  const value: MailContextType = {
    loading,
    error,
    emails,

    fetchMail,
    readEmail,
    sendEmail,
    clearError,
  };

  return <MailContext.Provider value={value}>{children}</MailContext.Provider>;
}
